import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import _ from 'lodash';
import { Mutation, Action } from 'vuex-module-decorators';
import { ApigatewayApi, EdgeEnrollmentApi, EdgeSiteAccountApi, EdgeAccountApi } from '@trialcard/apigateway.client';
import moment from 'moment';
import { Resolve } from 'vue-di';
import {
    MedicalClaimSearchResponseModel,
    PharmacyClaimSearchResponseModel,
    ScriptsSiteAccountV2PatientsPatientAccountModel,
    EnrollmentModel,
    PrescriberModel,
} from '@trialcard/apigateway.models';
import { EnrollmentV2PaymentTypeModel } from '@trialcard/apigateway.models/enrollmentV2PaymentTypeModel';
import {
    AccountExternalIdType,
    AccountRoleType,
    EnrollmentType,
    EnrollmentStatus,
    Gender,
    CaseExternalIdType,
    ExternalIdType,
} from '@trialcard/enums';
import AccountModule from './account';
import { TcPatientAccountDetailsModel, PatientAccountDisplayDetails } from './interfaces';
import SessionModule from './session';
import { SearchFields } from '~/components/patient/searchFields';
import { PatientSearchUniqueIdenitfierType, SearchEnrollmentStatus } from '~/types/searchTypes';

@InjectModule({ stateFactory: true }, module)
// @OnClear<SearchModule>(async value => value.clear())
export default class SearchModule extends InjectVuexModule {
    @Resolve
    siteAccountApi!: EdgeSiteAccountApi;
    @Resolve
    accountApi!: EdgeAccountApi;
    @Resolve
    accountModule!: AccountModule;
    @Resolve
    session!: SessionModule;
    @Resolve
    apiGateWay!: ApigatewayApi;
    @Resolve
    enrollmentApi!: EdgeEnrollmentApi;

    results: PatientAccountDisplayDetails[] = [];
    currentPatient!: PatientAccountDisplayDetails;
    currentEdit: any;
    currentDelete: any;
    paymentMethods!: EnrollmentV2PaymentTypeModel[];
    hubSearchResultPrescribers: PrescriberModel[] = [];

    setCurrentEdit(item: any): void {
        this.currentEdit = item;
    }
    setCurrentDelete(item: any): void {
        this.currentDelete = item;
    }
    getCurrentEdit() {
        return this.currentEdit;
    }
    setCurrentPatient(patient: PatientAccountDisplayDetails): void {
        this.currentPatient = patient;
    }
    getCurrentPatient() {
        return this.currentPatient;
    }

    @Mutation
    clearResults(): void {
        this.results = [];
        this.currentPatient = {} as PatientAccountDisplayDetails;
    }

    @Action({})
    async getSearchResultsByPatientDemographics(request: SearchFields) {
        return this.getSearchResults(request);
    }

    @Action({})
    async getSearchResultsByUniqueId(idSearch: { uniqueIdType: PatientSearchUniqueIdenitfierType; uniqueId: string }) {
        return this.getSearchResults(undefined, idSearch);
    }

    @Action({})
    private async setHubSearchResultAccountPrescribers(request: ScriptsSiteAccountV2PatientsPatientAccountModel[]) {
        const searchPrescriberIds: number[] = [];
        request.map(acct => {
            const prescriberIds = acct.account.prescribers.map(pr => {
                return pr.relatedId!;
            });
            searchPrescriberIds.push(...prescriberIds);
        });
        const prescribersApiResponse = await this.accountApi.prescribersHubGetPrescribers({ prescriberIds: searchPrescriberIds });
        const prescriberData = (prescribersApiResponse && prescribersApiResponse.data && prescribersApiResponse.data.data) || [];
        this.setHubSearchResultPrescribers(prescriberData);
    }

    @Mutation
    setHubSearchResultPrescribers(data: PrescriberModel[]) {
        this.hubSearchResultPrescribers = data;
    }

    private async getSearchResults(
        request?: SearchFields,
        idSearch?: { uniqueIdType: PatientSearchUniqueIdenitfierType; uniqueId: string }
    ) {
        this.clearResults();
        let accountExternalIdType;
        let accountExternalId;
        let searchResultsResponse;
        const accountResults: PatientAccountDisplayDetails[] = [];

        if (request) {
            accountExternalIdType = this.session['https://apigateway.trialcard.com/account']
                ? AccountExternalIdType.SiteAccountId
                : undefined;

            accountExternalId = this.session['https://apigateway.trialcard.com/account']
                ? this.session['https://apigateway.trialcard.com/account'].toString()
                : undefined;
        } else if (idSearch && idSearch.uniqueIdType === PatientSearchUniqueIdenitfierType.AccountNumber) {
            accountExternalIdType = this.getExternalIdType(idSearch.uniqueIdType) as AccountExternalIdType;
            accountExternalId = idSearch.uniqueId;
        }

        const apiRequest = {
            maxRecordCount: request ? 500 : 10,
            firstName: (request || {}).firstName || undefined,
            lastName: (request || {}).lastName || undefined,
            dateOfBirth: (request || {}).dateOfBirth ? moment((request || {}).dateOfBirth || '') : undefined,
            state: (request || {}).state || undefined,
            prescriberId: (request || {}).prescriber || undefined,
            accountExternalIdType,
            accountExternalId,
        };

        if (request || (idSearch && idSearch.uniqueIdType === PatientSearchUniqueIdenitfierType.AccountNumber)) {
            searchResultsResponse = this.session.isHub
                ? await this.siteAccountApi.patientsSearchAllProgramPatients(apiRequest)
                : await this.siteAccountApi.patientsSearchPatients(apiRequest);
        } else if (idSearch && idSearch.uniqueIdType !== PatientSearchUniqueIdenitfierType.AccountNumber) {
            apiRequest.accountExternalIdType = this.getExternalIdType(idSearch.uniqueIdType);
            apiRequest.accountExternalId = idSearch.uniqueId;
            searchResultsResponse = this.session.isHub
                ? await this.siteAccountApi.patientsSearchAllProgramPatients(apiRequest)
                : await this.siteAccountApi.patientsSearchPatients(apiRequest);
        }

        const results = (searchResultsResponse || {}).data && ((searchResultsResponse || {}).data || {}).data;
        if (!results || results.length === 0) {
            return [];
        }

        await Promise.all(
            results.map(async accts => {
                return this.getPatientAccountDetails(accts).then(response => {
                    accountResults.push(response);
                    return;
                });
            })
        );
        return accountResults;
    }

    private async getPatientAccountDetails(
        patientAccount: ScriptsSiteAccountV2PatientsPatientAccountModel
    ): Promise<PatientAccountDisplayDetails> {
        // Get the patient's enrollment
        const enrollment = await this.accountModule
            .getEnrollment({
                ...patientAccount.patient,
                zip:
                    patientAccount.patient.addresses &&
                    patientAccount.patient.addresses.length > 0 &&
                    patientAccount.patient.addresses[0].zip!,
            } as any)
            .then(response => {
                return response;
            })
            .catch(() => {
                return undefined;
            });

        // Find Primary Medical Payor from payorlist
        const medicalPayors =
            patientAccount.account.payors &&
            patientAccount.account.payors.length > 0 &&
            patientAccount.account.payors.find(py => py.roleType === AccountRoleType.InsuranceProvider);
        const primaryMedicalPayor =
            (medicalPayors && medicalPayors.accountRelations && medicalPayors.accountRelations.find(x => x.coverageOrder === 1)) || {};
        const patientAccountDetails = {
            patientAccount,
            enrollment,
        } as TcPatientAccountDetailsModel;
        return this.mapToPatientAccountDisplay(patientAccountDetails);
    }

    @Action({})
    async getClaims(enrollment: EnrollmentModel) {
        const enrollmentMedicalClaims: MedicalClaimSearchResponseModel[] = [];
        const enrollmentPharmacyClaims: PharmacyClaimSearchResponseModel[] = [];
        if (enrollment) {
            // Get Claims
            const medicalMemberNumber = enrollment.memberNumbers.find(x => {
                return x.type === EnrollmentType.Medical;
            });
            const pharmacyMemberNumber = enrollment.memberNumbers.find(x => {
                return x.type === EnrollmentType.Pharmacy;
            });
            if (medicalMemberNumber) {
                const medicalClaims = await this.accountModule.getMedicalClaims(medicalMemberNumber.number!);
                enrollmentMedicalClaims.push(...medicalClaims);
            }
            if (pharmacyMemberNumber) {
                const pharmacyClaims = await this.accountModule.getPharmacyClaims(pharmacyMemberNumber.number!);
                enrollmentPharmacyClaims.push(...pharmacyClaims);
            }
        }
        return {
            claims: [...enrollmentMedicalClaims, ...enrollmentPharmacyClaims],
        };
    }

    @Action
    async ResendSignatureRequest(accountId: number): Promise<boolean> {
        const request = {
            accountId,
        };

        const isSuccessful = await this.apiGateWay.edgeSignature
            .envelopeResendLatest(request)
            .then(res => {
                if (res.data && res.data.success) {
                    return true;
                } else {
                    return false;
                }
            })
            .catch(() => {
                return false;
            });

        return isSuccessful;
    }

    private mapToPatientAccountDisplay(patientAccountDetails: TcPatientAccountDetailsModel): PatientAccountDisplayDetails {
        const patient = patientAccountDetails.patientAccount.patient;
        const enrollment = patientAccountDetails.enrollment;
        const medicalMemberNumber =
            enrollment &&
            enrollment!.memberNumbers.find(x => {
                return x.type === EnrollmentType.Medical;
            });
        const pharmacyMemberNumber =
            enrollment &&
            enrollment!.memberNumbers.find(x => {
                return x.type === EnrollmentType.Pharmacy;
            });
        const address = patient.addresses && patient.addresses.length > 0 && patient.addresses[0] ? patient.addresses[0] : undefined;
        const enrollmentType = (enrollment && enrollment.bestEnrollmentType) || '';
        const enrollmentStatus =
            (enrollment && enrollment.status && Number(enrollment.status.status) >= 0 && EnrollmentStatus[enrollment.status.status!]) ||
            enrollmentType;
        const enrollmentStatusDisplay =
            (enrollment &&
                enrollment.status &&
                Number(enrollment.status.status) >= 0 &&
                SearchEnrollmentStatus[enrollment.status.status!]) ||
            enrollmentType;

        return {
            patientId: patient.patientId,
            firstName: patient.firstName,
            lastName: patient.lastName,
            dateOfBirth: patient.dateOfBirth && moment(patient.dateOfBirth).format('L'),
            addressDisplay: `${address && address.addressOne} ${address && address.city}, ${address && address.state} ${address &&
                address.zip}`,
            addresses: patient.addresses,
            addressOne: address && address.addressOne,
            addressTwo: address && address.addressTwo,
            city: address && address.city,
            state: address && address.state,
            zipCode: address && address.zip,
            gender: patient.gender ? this.getGender(patient.gender.valueOf()) : '',
            genderValue: patient.gender || 0,
            aobFlag: patientAccountDetails.patientAccount.account.assignmentOfBenefitFlag ? 'Y' : 'N',
            accountId: patientAccountDetails.patientAccount.account.displayId,
            accountIdNumber: patientAccountDetails.patientAccount.account.accountId,
            thirdPartyId:
                (
                    patientAccountDetails.patientAccount.account.externalIds.find(item => {
                        return item.externalIdType === AccountExternalIdType.THIRDPARTYID.valueOf();
                    }) || {}
                ).externalId || '',
            medicalMemberId: medicalMemberNumber && medicalMemberNumber.number,
            paymentType:
                ((this.paymentMethods || []).find(item => (enrollment || {}).paymentType === item.paymentTypeId) || {})
                    .paymentTypeDescription || '',
            pharmacyMemberId: pharmacyMemberNumber && pharmacyMemberNumber.number,
            sites: patientAccountDetails.patientAccount.account.sites,
            enrollmentEndDate: enrollment && enrollment.endDate && moment(enrollment.endDate).format('L'),
            enrollmentStartDate: enrollment && enrollment.startDate && moment(enrollment.startDate).format('L'),
            enrollment: enrollment || {},
            phoneNumbers: patient.phoneNumbers,
            emailAddresses: patient.emailAddresses,
            enrollmentStatus,
            enrollmentStatusDisplay,
            enrollmentType,
        } as PatientAccountDisplayDetails;
    }

    getGender(gender: number) {
        let result;
        switch (gender) {
            case Gender.UNKN:
                result = 'Unkown';
                break;
            case Gender.M:
                result = 'Male';
                break;
            case Gender.F:
                result = 'Female';
                break;
            case Gender.I:
                result = 'Indeterminate';
                break;
        }
        return result;
    }

    getExternalIdType(uniqueIdType: PatientSearchUniqueIdenitfierType) {
        let result;
        switch (uniqueIdType) {
            case PatientSearchUniqueIdenitfierType.MedicalMemberId:
                result = AccountExternalIdType.MedMemberID;
                break;
            case PatientSearchUniqueIdenitfierType.PharmacyMemberId:
                result = AccountExternalIdType.PharMemberID;
                break;
            case PatientSearchUniqueIdenitfierType.AccountNumber:
                result = AccountExternalIdType.TrialCardHubId;
                break;
            case PatientSearchUniqueIdenitfierType.ThirdPartyId:
                result = AccountExternalIdType.THIRDPARTYID;
                break;
        }
        return result;
    }

    @Action({})
    async getPaymentTypes() {
        try {
            const paymentMethods = await this.enrollmentApi.paymentPaymentMethod();

            this.paymentMethods = (((paymentMethods || {}).data || {}).data || {}).data;

            return this.paymentMethods;
        } catch (error) {
            return [];
        }
    }
}
